// src/pages/BookDetailPage.js

import React, { useEffect, useRef, useCallback } from 'react'; // <-- Added useCallback
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { books } from '../data/books';
import '../styles/BookDetailPage.css';

// --- NEW ---
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase'; // Ensure this path is correct

// --- ICONS ---
import {
  FaCalendarAlt,
  FaTag,
  FaBuilding,
  FaBarcode,
  FaChevronRight,
  FaQuoteLeft,
} from 'react-icons/fa';

// --- ANIMATION VARIANTS ---
const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1, transition: { duration: 0.8, ease: 'easeInOut' } },
  out: { opacity: 0, transition: { duration: 0.5, ease: 'easeInOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const fadeInUp = {
  hidden: { y: 40, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
};

const imageVariants = {
    hidden: { scale: 1.05, opacity: 0 },
    visible: {
        scale: 1,
        opacity: 1,
        transition: {
            duration: 1.2,
            ease: [0.22, 1, 0.36, 1]
        }
    }
}


// --- HELPER COMPONENTS ---

const ScrollProgressBar = () => {
  const { scrollYProgress } = useScroll({}, { layoutEffect: false });
  return <motion.div className="scroll-progress-bar" style={{ scaleX: scrollYProgress }} />;
};

const DetailItem = ({ icon, label, value }) => (
  <motion.div className="bd-detail-item" variants={fadeInUp}>
    <div className="bd-detail-icon">{icon}</div>
    <div className="bd-detail-text">
      <span className="bd-detail-label">{label}</span>
      <span className="bd-detail-value">{value}</span>
    </div>
  </motion.div>
);

// --- CORRECTED TagTicker Component ---
const TagTicker = ({ tags }) => {
    const extendedTags = [...tags, ...tags];

    return (
      <div className="bd-ticker-wrap" aria-hidden="true">
        <motion.div
          className="bd-ticker-track"
          animate={{ x: ['0%', '-50%'] }}
          transition={{
            ease: 'linear',
            duration: 50,
            repeat: Infinity,
          }}
        >
          {extendedTags.map((tag, i) => (
            <div key={`ticker-${i}`} className="bd-ticker-item">
              {/* DEFINITIVE FIX: Display the tag text exactly as is, with spaces. */}
              <span>{tag}</span>
            </div>
          ))}
        </motion.div>
      </div>
    );
  };


// --- MAIN DETAIL PAGE COMPONENT ---
const BookDetailPage = () => {
  const { bookId } = useParams();
  const book = books.find((b) => b.slug === bookId);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]); 
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '200%']);
  const textOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // --- NEW ---
  const handleRetailerClick = useCallback((retailerName) => {
    if (!book) return; // Safety check

    logEvent(analytics, 'click_buy_on_retailer', {
      item_id: book.slug,
      item_name: book.title,
      retailer_name: retailerName,
    });
  }, [book]);

  if (!book) {
    return (
      <div className="book-not-found">
        <h2>Book Not Found</h2>
        <p>Sorry, we couldn't find the book you were looking for.</p>
        <Link to="/" className="bd-retailer-link">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <ScrollProgressBar />
      <motion.div
        className="book-detail-page"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
      >
        <motion.section ref={heroRef} className="bd-hero-section">
          <motion.div className="bd-hero-bg" style={{ scale: backgroundScale, backgroundImage: `url(${book.coverUrl})` }} />
          <div className="bd-hero-overlay" />
          <motion.div className="bd-hero-content" style={{ y: textY, opacity: textOpacity }}>
            <motion.h1 className="bd-title" variants={fadeInUp}>
              {book.title}
            </motion.h1>
            <motion.p className="bd-keyphrase" variants={fadeInUp}>
              "{book.keyPhrase}"
            </motion.p>
          </motion.div>
        </motion.section>

        <main className="bd-content-section">
          <aside className="bd-sticky-column">
            <motion.div className="bd-sticky-content-wrapper" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerContainer}>
                <motion.img
                    src={book.coverUrl}
                    alt={`${book.title} cover`}
                    className="bd-cover-image-main"
                    variants={imageVariants}
                />
                <motion.div className="bd-details-widget" variants={fadeInUp}>
                    <h3 className="bd-widget-title">Book Details</h3>
                    <DetailItem icon={<FaTag />} label="Genre" value={book.details.genre} />
                    <DetailItem icon={<FaCalendarAlt />} label="Release Date" value={book.details.releaseDate} />
                    <DetailItem icon={<FaBuilding />} label="Publisher" value={book.details.publisher} />
                    <DetailItem icon={<FaBarcode />} label="ISBN" value={book.details.isbn} />
                </motion.div>
                {book.productDetails?.length > 0 && (
                  <motion.div className="bd-details-widget bd-product-details" variants={fadeInUp}>
                    <h3 className="bd-widget-title">Product Details</h3>
                    <ul className="bd-product-details-list">
                      {book.productDetails.map((detail) => (
                        <li className="bd-product-detail-row" key={detail.label}>
                          <span className="bd-product-detail-label">{detail.label}</span>
                          <span className="bd-product-detail-value">{detail.value}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
                <motion.div className="bd-purchase-widget" variants={fadeInUp}>
                    <h3 className="bd-widget-title">Get Your Copy</h3>
                    {book.retailers.map((retailer) => (
                    <a
                        key={retailer.name}
                        href={retailer.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bd-retailer-link"
                        onClick={() => handleRetailerClick(retailer.name)} // <-- NEW
                    >
                        <span>Buy on {retailer.name}</span>
                        <FaChevronRight />
                    </a>
                    ))}
                </motion.div>
            </motion.div>
          </aside>

          <article className="bd-scrollable-content">
            <motion.section
              className="bd-synopsis"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp}>Synopsis</motion.h2>
              {book.synopsis.map((paragraph, index) => (
                <motion.p key={index} variants={fadeInUp}>
                  {paragraph}
                </motion.p>
              ))}
            </motion.section>
            
            <motion.section className="bd-reviews-section"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp}>Praise & Reviews</motion.h2>
              {book.reviews.length > 0 ? (
                <div className="bd-reviews-grid">
                  {book.reviews.map((review) => (
                    <motion.div key={review.id} className="bd-review-card" variants={fadeInUp}>
                      <FaQuoteLeft className="bd-review-quote-icon" />
                      <p>"{review.review}"</p>
                      <footer className="bd-review-attribution">
                        — {review.name}
                      </footer>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.p className="bd-reviews-empty" variants={fadeInUp}>
                  No praise or reviews yet — check back soon.
                </motion.p>
              )}
            </motion.section>
          </article>
        </main>

        <section className="bd-ticker-section">
            <TagTicker tags={book.tags} />
        </section>

      </motion.div>
    </>
  );
};

export default BookDetailPage;